import { createId } from "@paralleldrive/cuid2";
import { Plus, Trash2 } from "lucide-react";
import { useFieldArray, useFormContext } from "react-hook-form";

import type { ModifierFormSchema } from "../../../lib/validators/modifiers";
import { FormControl, FormField, FormItem, FormMessage } from "../../ui/form";
import { Input } from "../../ui/input";
import { Switch } from "../../ui/switch";
import { PriceFieldSub } from "./price-field";

interface SubmodifierModifiersListFormProps {
  index: number;
}

export function SubmodifierModifiersListForm({
  index,
}: SubmodifierModifiersListFormProps) {
  const form = useFormContext<ModifierFormSchema>();
  const modifiersList = useFieldArray({
    control: form.control,
    name: `modifiersList.${index}.data.modifiers`,
  });

  return (
    <div>
      <table className="w-full border-separate border-spacing-2">
        <thead>
          <tr className="text-left">
            <th className="w-40">Modifier name</th>
            <th className="w-24">Price</th>
            <th className="w-24">Out of stock</th>
            <th className="w-24">Multi Quantity</th>
            <th className="w-24">Delete</th>
          </tr>
        </thead>
        <tbody>
          {modifiersList.fields.map((field, i) => (
            <tr key={field.id}>
              <td>
                <FormField
                  control={form.control}
                  name={`modifiersList.${index}.data.modifiers.${i}.name`}
                  render={({ field: formField }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          {...formField}
                          className="body-sm placeholder:body-sm h-9 rounded-md px-3 py-2 leading-tight"
                          placeholder="New modifier"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </td>
              <td>
                <FormField
                  control={form.control}
                  name={`modifiersList.${index}.data.modifiers.${i}.additionalCost`}
                  render={({ field: formField }) => (
                    <FormItem>
                      <FormControl>
                        <PriceFieldSub field={formField} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </td>
              <td>
                <FormField
                  control={form.control}
                  name={`modifiersList.${index}.data.modifiers.${i}.isOutOfStock`}
                  render={({ field: formField }) => (
                    <FormItem>
                      <FormControl>
                        <Switch
                          checked={formField.value}
                          onCheckedChange={formField.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </td>
              <td>
                <FormField
                  control={form.control}
                  name={`modifiersList.${index}.data.modifiers.${i}.canBeMultiplied`}
                  render={({ field: formField }) => (
                    <FormItem>
                      <FormControl>
                        <Switch
                          checked={formField.value}
                          onCheckedChange={formField.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </td>
              <td>
                <Trash2
                  className="cursor-pointer"
                  onClick={() => modifiersList.remove(i)}
                  type="button"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        className="text-success mt-4 flex items-center gap-1"
        onClick={() => {
          modifiersList.append({
            itemId: createId(),
            name: "",
            additionalCost: "",
            sortOrder: 0,
            canBeMultiplied: false,
            isOutOfStock: false,
          });
        }}
        type="button"
      >
        <Plus className="size-4" />
        Add modifier
      </button>
    </div>
  );
}
